import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Grid, Alert, Button, Skeleton, IconButton,
    ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField,
} from '@mui/material';
import {
    ErrorOutline as ErrorIcon,
    WarningAmberOutlined as WarningIcon,
    InfoOutlined as InfoIcon,
    CheckCircleOutline as CheckIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    BoltOutlined as BoltIcon,
} from '@mui/icons-material';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import { supabase } from '../../api/supabase';
import { adminTokens as T } from '../../theme/adminTokens';

const DRAWER_WIDTH = 260;
type Period = '1h' | '24h' | '7d' | '30d';

interface MetricSummary {
    count: number;
    sum: number;
    min: number;
    max: number;
    avg: number;
    unit: string;
}

interface MonitoringAlert {
    id: string;
    level: 'critical' | 'warning' | 'info' | string;
    type: string;
    message: string;
    metadata: any;
    resolved: boolean;
    created_at: string;
}

interface MonitoringData {
    period: { start: string; end: string; hours: number };
    health_score: number;
    metrics: Record<string, MetricSummary>;
    alerts: {
        critical: { count: number; items: MonitoringAlert[] };
        warning:  { count: number; items: MonitoringAlert[] };
        info:     { count: number; items: MonitoringAlert[] };
    };
    recent_errors: any[];
    thresholds: Record<string, number>;
}

const formatMetricName = (s: string) =>
    s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatTimeAgo = (iso: string) => {
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

const healthColor = (score: number) => {
    if (score >= 90) return T.color.success.base;
    if (score >= 70) return '#8BC34A';
    if (score >= 50) return T.color.warning.base;
    return T.color.error.base;
};
const healthLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
};

const HealthRing: React.FC<{ score: number; loading?: boolean }> = ({ score, loading }) => {
    const size = 132;
    const stroke = 10;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);
    const color = healthColor(score);

    if (loading) return <Skeleton variant="circular" width={size} height={size} />;

    return (
        <Box sx={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} aria-hidden role="img">
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke={T.color.surface.sunken} strokeWidth={stroke}
                />
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke={color} strokeWidth={stroke}
                    strokeDasharray={circumference} strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{
                        transition: `stroke-dashoffset ${T.motion.medium}`,
                    }}
                />
            </svg>
            <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
            }}>
                <Typography sx={{
                    fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                    color: T.color.text.primary, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                }}>{score}</Typography>
                <Typography sx={{
                    fontSize: T.type.size.xs, color: T.color.text.tertiary,
                    mt: '2px',
                }}>/ 100</Typography>
            </Box>
        </Box>
    );
};

const SeverityIcon: React.FC<{ level: string; size?: number }> = ({ level, size = 18 }) => {
    if (level === 'critical') return <ErrorIcon sx={{ fontSize: size, color: T.color.error.base }} />;
    if (level === 'warning')  return <WarningIcon sx={{ fontSize: size, color: T.color.warning.base }} />;
    return <InfoIcon sx={{ fontSize: size, color: T.color.info.base }} />;
};

const severityTone = (level: string) => {
    if (level === 'critical') return { bg: T.color.error.soft,   text: T.color.error.text };
    if (level === 'warning')  return { bg: T.color.warning.soft, text: T.color.warning.text };
    return { bg: T.color.info.soft, text: T.color.info.text };
};

const MonitoringDashboard: React.FC = () => {
    const [dashboard, setDashboard] = useState<MonitoringData | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [period, setPeriod] = useState<Period>('24h');

    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<MonitoringAlert | null>(null);
    const [resolution, setResolution] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchDashboard = async (opts: { silent?: boolean } = {}) => {
        try {
            if (opts.silent) setRefreshing(true); else setLoading(true);
            setError(null);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            const response = await fetch(`/api/admin/monitoring/dashboard?period=${period}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch monitoring dashboard');
            const json = await response.json();
            setDashboard(json.data);
        } catch (err: any) {
            setError(err.message || 'Failed to load monitoring data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
        const id = setInterval(() => fetchDashboard({ silent: true }), 60000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    const openResolve = (a: MonitoringAlert) => {
        setSelectedAlert(a);
        setResolution('');
        setResolveDialogOpen(true);
    };
    const closeResolve = () => {
        setResolveDialogOpen(false);
        setSelectedAlert(null);
        setResolution('');
    };
    const submitResolve = async () => {
        if (!selectedAlert || !resolution.trim()) return;
        try {
            setSubmitting(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            const response = await fetch('/api/admin/monitoring/alerts/resolve', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({ alertId: selectedAlert.id, resolution: resolution.trim() }),
            });
            if (!response.ok) throw new Error('Failed to resolve alert');
            await fetchDashboard({ silent: true });
            closeResolve();
        } catch (err: any) {
            setError(err.message || 'Failed to resolve alert');
        } finally {
            setSubmitting(false);
        }
    };

    const sortedAlerts = useMemo(() => {
        if (!dashboard) return [] as MonitoringAlert[];
        return [
            ...dashboard.alerts.critical.items.map(a => ({ ...a, level: 'critical' as const })),
            ...dashboard.alerts.warning.items.map(a => ({ ...a, level: 'warning' as const })),
            ...dashboard.alerts.info.items.slice(0, 5).map(a => ({ ...a, level: 'info' as const })),
        ];
    }, [dashboard]);

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Page header + period chips + refresh */}
                <Box sx={{ mb: `${T.space[6]}px`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: `${T.space[3]}px` }}>
                    <Box>
                        <Typography sx={{
                            fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                            color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                        }}>System monitoring</Typography>
                        <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                            Performance metrics, alerts, and system health at a glance. Auto-refreshes every minute.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: `${T.space[2]}px`, alignItems: 'center' }}>
                        <ToggleButtonGroup
                            value={period}
                            exclusive
                            onChange={(_, v) => v && setPeriod(v)}
                            size="small"
                            sx={{
                                bgcolor: T.color.surface.raised,
                                border: `1px solid ${T.color.surface.border}`,
                                borderRadius: `${T.radius.md}px`,
                                p: '2px',
                                '& .MuiToggleButton-root': {
                                    border: 'none',
                                    color: T.color.text.secondary,
                                    fontWeight: T.type.weight.semibold,
                                    fontSize: T.type.size.sm,
                                    textTransform: 'none',
                                    px: `${T.space[3]}px`,
                                    py: `${T.space[1]}px`,
                                    borderRadius: `${T.radius.sm}px !important`,
                                    '&.Mui-selected': { bgcolor: T.color.brand[50], color: T.color.brand[700] },
                                    '&:hover': { bgcolor: T.color.surface.sunken },
                                },
                            }}
                        >
                            <ToggleButton value="1h">1 h</ToggleButton>
                            <ToggleButton value="24h">24 h</ToggleButton>
                            <ToggleButton value="7d">7 d</ToggleButton>
                            <ToggleButton value="30d">30 d</ToggleButton>
                        </ToggleButtonGroup>
                        <IconButton
                            onClick={() => fetchDashboard({ silent: true })}
                            disabled={loading || refreshing}
                            aria-label="Refresh"
                            sx={{
                                color: T.color.text.secondary,
                                border: `1px solid ${T.color.surface.border}`,
                                borderRadius: `${T.radius.md}px`,
                            }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: `${T.space[4]}px`, borderRadius: `${T.radius.md}px` }}
                        action={<Button color="inherit" size="small" onClick={() => fetchDashboard()}>Retry</Button>}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Top row: health + alerts KPIs */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.lg}px`,
                            p: `${T.space[5]}px`,
                            boxShadow: T.shadow.sm,
                            height: '100%',
                            display: 'flex', alignItems: 'center', gap: `${T.space[5]}px`,
                        }}>
                            <HealthRing score={dashboard?.health_score ?? 0} loading={loading} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                    color: T.color.text.secondary, textTransform: 'uppercase', letterSpacing: 0.4,
                                }}>System health</Typography>
                                {loading ? (
                                    <Skeleton variant="text" width="40%" height={36} />
                                ) : (
                                    <Typography sx={{
                                        fontSize: T.type.size.xl, fontWeight: T.type.weight.bold,
                                        color: healthColor(dashboard?.health_score ?? 0),
                                        lineHeight: T.type.lineHeight.tight, mt: `${T.space[1]}px`,
                                    }}>
                                        {healthLabel(dashboard?.health_score ?? 0)}
                                    </Typography>
                                )}
                                <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                                    {loading
                                        ? 'Calculating system status…'
                                        : (dashboard && dashboard.health_score >= 70)
                                            ? 'System is operating normally.'
                                            : 'System is experiencing issues — see active alerts below.'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <KPIStatCard
                            label="Critical"
                            value={dashboard?.alerts.critical.count}
                            icon={<ErrorIcon />}
                            tone="error"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <KPIStatCard
                            label="Warnings"
                            value={dashboard?.alerts.warning.count}
                            icon={<WarningIcon />}
                            tone="warning"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <KPIStatCard
                            label="Info"
                            value={dashboard?.alerts.info.count}
                            icon={<InfoIcon />}
                            tone="info"
                            loading={loading}
                        />
                    </Grid>
                </Grid>

                {/* Metrics grid */}
                <Box sx={{ mb: `${T.space[5]}px` }}>
                    <Typography sx={{
                        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                        color: T.color.text.secondary, textTransform: 'uppercase', letterSpacing: 0.4,
                        mb: `${T.space[3]}px`,
                    }}>Performance metrics</Typography>
                    {loading ? (
                        <Grid container spacing={3}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: `${T.radius.lg}px` }} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : dashboard && Object.keys(dashboard.metrics).length > 0 ? (
                        <Grid container spacing={3}>
                            {Object.entries(dashboard.metrics).map(([name, metric]) => (
                                <Grid item xs={12} sm={6} md={4} key={name}>
                                    <Box sx={{
                                        bgcolor: T.color.surface.raised,
                                        border: `1px solid ${T.color.surface.border}`,
                                        borderRadius: `${T.radius.lg}px`,
                                        p: `${T.space[5]}px`,
                                        boxShadow: T.shadow.sm,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: `${T.space[2]}px` }}>
                                            <Typography sx={{
                                                fontSize: T.type.size.sm, fontWeight: T.type.weight.medium,
                                                color: T.color.text.secondary, letterSpacing: 0.2,
                                            }}>{formatMetricName(name)}</Typography>
                                            <Box sx={{
                                                width: 28, height: 28, borderRadius: `${T.radius.md}px`,
                                                bgcolor: T.color.brand[50], color: T.color.brand[600],
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <BoltIcon sx={{ fontSize: 16 }} />
                                            </Box>
                                        </Box>
                                        <Typography sx={{
                                            fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                                            color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}>
                                            {metric.avg.toFixed(2)}<Typography component="span" sx={{ fontSize: T.type.size.md, color: T.color.text.tertiary, ml: `${T.space[1]}px`, fontWeight: T.type.weight.regular }}>{metric.unit}</Typography>
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex', justifyContent: 'space-between', gap: `${T.space[2]}px`,
                                            mt: `${T.space[3]}px`, pt: `${T.space[3]}px`,
                                            borderTop: `1px solid ${T.color.surface.border}`,
                                        }}>
                                            <MetricStat label="Min" value={metric.min.toFixed(2)} />
                                            <MetricStat label="Max" value={metric.max.toFixed(2)} />
                                            <MetricStat label="N" value={metric.count.toLocaleString()} />
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.lg}px`,
                            boxShadow: T.shadow.sm,
                            py: `${T.space[6]}px`, textAlign: 'center',
                            color: T.color.text.tertiary, fontSize: T.type.size.sm,
                        }}>
                            No metrics captured in this period.
                        </Box>
                    )}
                </Box>

                {/* Active alerts panel */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        p: `${T.space[4]}px`,
                        borderBottom: `1px solid ${T.color.surface.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: `${T.space[3]}px`,
                    }}>
                        <Typography sx={{
                            fontSize: T.type.size.lg, fontWeight: T.type.weight.semibold,
                            color: T.color.text.primary,
                        }}>Active alerts</Typography>
                        {dashboard && (
                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>
                                Period: last {dashboard.period.hours} h
                            </Typography>
                        )}
                    </Box>
                    {loading ? (
                        <Box sx={{ p: `${T.space[4]}px`, display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: `${T.radius.md}px` }} />
                            ))}
                        </Box>
                    ) : sortedAlerts.length === 0 ? (
                        <Box sx={{ py: `${T.space[7]}px`, textAlign: 'center' }}>
                            <CheckIcon sx={{ fontSize: 48, color: T.color.success.base, mb: `${T.space[2]}px` }} />
                            <Typography sx={{
                                fontSize: T.type.size.md, fontWeight: T.type.weight.semibold,
                                color: T.color.text.primary,
                            }}>All systems operational</Typography>
                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                                No active alerts in this period.
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {sortedAlerts.map(a => {
                                const tone = severityTone(a.level);
                                return (
                                    <Box key={a.id} sx={{
                                        display: 'flex', alignItems: 'center', gap: `${T.space[3]}px`,
                                        p: `${T.space[4]}px`,
                                        borderBottom: `1px solid ${T.color.surface.border}`,
                                        '&:last-of-type': { borderBottom: 'none' },
                                    }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: `${T.radius.md}px`,
                                            bgcolor: tone.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <SeverityIcon level={a.level} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[1]}px` }}>
                                                <Box sx={{
                                                    display: 'inline-flex',
                                                    px: `${T.space[2]}px`, py: '2px',
                                                    bgcolor: tone.bg, color: tone.text,
                                                    borderRadius: `${T.radius.pill}px`,
                                                    fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                                    textTransform: 'capitalize',
                                                }}>{a.level}</Box>
                                                <Typography sx={{
                                                    fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold,
                                                    color: T.color.text.primary,
                                                }}>
                                                    {a.type.replace(/_/g, ' ')}
                                                </Typography>
                                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, ml: 'auto', whiteSpace: 'nowrap' }}>
                                                    {formatTimeAgo(a.created_at)}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{
                                                fontSize: T.type.size.sm, color: T.color.text.secondary,
                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            }}>
                                                {a.message}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openResolve(a)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: T.type.weight.semibold,
                                                borderColor: T.color.surface.border,
                                                color: T.color.text.secondary,
                                                '&:hover': { borderColor: T.color.brand[300], color: T.color.brand[600] },
                                            }}
                                        >Resolve</Button>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Resolve alert dialog */}
            <Dialog
                open={resolveDialogOpen}
                onClose={closeResolve}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: `${T.radius.lg}px` } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                            Resolve alert
                        </Typography>
                        {selectedAlert && (
                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                                {selectedAlert.type.replace(/_/g, ' ')} · {formatTimeAgo(selectedAlert.created_at)}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={closeResolve} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: `${T.space[5]}px` }}>
                    {selectedAlert && (
                        <Box sx={{
                            p: `${T.space[3]}px`,
                            bgcolor: severityTone(selectedAlert.level).bg,
                            color: severityTone(selectedAlert.level).text,
                            borderRadius: `${T.radius.md}px`,
                            mb: `${T.space[4]}px`,
                            fontSize: T.type.size.sm,
                        }}>
                            {selectedAlert.message}
                        </Box>
                    )}
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        label="Resolution notes"
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Describe how this alert was resolved…"
                    />
                </DialogContent>
                <DialogActions sx={{ p: `${T.space[3]}px` }}>
                    <Button onClick={closeResolve} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        onClick={submitResolve}
                        variant="contained"
                        disabled={submitting || !resolution.trim()}
                        sx={{
                            textTransform: 'none',
                            fontWeight: T.type.weight.semibold,
                            bgcolor: T.color.brand[500],
                            '&:hover': { bgcolor: T.color.brand[600] },
                        }}
                    >
                        {submitting ? 'Resolving…' : 'Resolve alert'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const MetricStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography sx={{
            fontSize: T.type.size.xs, color: T.color.text.tertiary,
            textTransform: 'uppercase', letterSpacing: 0.4,
            fontWeight: T.type.weight.semibold,
        }}>{label}</Typography>
        <Typography sx={{
            fontSize: T.type.size.sm, color: T.color.text.primary,
            fontVariantNumeric: 'tabular-nums', mt: `${T.space[1]}px`,
        }}>{value}</Typography>
    </Box>
);

export default MonitoringDashboard;
